<template>
  <div>
    <div
      class="bg-card text-left"
      no-body
    >
      <b-card-body class="p-1">
        <h1 style="font-size: 1rem !important">
          {{ $t('pages.admin.promos.create-new-promo', 'Add new promotion') }}
        </h1>
        <div class="p-3">
          <div class="row">
            <div class="col-12 col-lg-6">
              <label style="font-size: 1.1rem">
                {{ $t('pages.admin.promos.form.code', 'Promo code') }}
              </label>
              <b-input
                v-model="promo.code"
                :placeholder="$t('pages.admin.promos.form.code.placeholder', 'Code goes here...')"
                :state="codeState"
                size="sm"
                style="text-transform: uppercase"
              />
            </div>
            <div class="col-12 col-lg-6">
              <label style="font-size: 1.1rem">
                {{ $t('pages.admin.promos.form.type', 'Promo type') }}
              </label>
              <b-form-select
                v-model="promo.type"
                :options="typeOptions"
                :state="typeState"
                size="sm"
              />
            </div>
            <div class="col-12 col-lg-6">
              <label style="font-size: 1.1rem">
                {{ $t('pages.admin.promos.form.discount', 'Discount') }}
              </label>
              <b-input
                v-model="promo.discount"
                placeholder="100"
                :state="discountState"
                size="sm"
              />
              <div style="font-size: 0.7rem">
                <div style="font-size: 0.8rem">
                  {{ $t('pages.admin.promos.form.discount.caption', '') }}
                </div>
                <div>{{ $t('pages.admin.promos.form.discount.caption1', '') }}</div>
                <div>{{ $t('pages.admin.promos.form.discount.caption2', '') }}</div>
              </div>
            </div>
            <div class="col-12 col-lg-6">
              <label style="font-size: 1.1rem">{{ $t('pages.admin.promos.form.limit', 'Use limit') }}</label>
              <b-input
                v-model="promo.limit"
                placeholder="100"
                :state="limitState"
                size="sm"
              />
            </div>
            <div class="col-12">
              <label style="font-size: 1.1rem">
                {{ $t('pages.admin.promos.form.user', 'For user') }}
                <b-checkbox
                  v-model="promo.forUser"
                  inline
                />
              </label>
              <model-select
                v-model="promo.user"
                :placeholder="$t('pages.admin.promos.form.user.placeholder', 'Select a user')"
                option-value="value"
                option-text="text"
                :options="userOptions"
                :is-disabled="!promo.forUser"
                :is-error="(isFormSubmitted && promo.forUser) && !promo.user"
                @searchchange="onSearchChange"
              />
            </div>
            <div class="col-3 mx-auto text-center">
              <div class="mt-4" />
              <b-button
                class="col btn-success"
                size="sm"
                :disabled="!readyToSend"
                @click="addPromo()"
              >
                {{ $t('generic.add', 'Add') }}
              </b-button>
            </div>
          </div>
        </div>
      </b-card-body>
    </div>
    <div
      class="bg-card text-left"
      no-body
    >
      <b-card-body class="p-1">
        <h1 style="font-size: 1rem !important">
          {{ $t('pages.admin.promos.existing-promos', 'Existing promos') }}
        </h1>
        <div class="p-3">
          <div class="text-left mb-2">
            <span style="font-size: 0.8rem;">{{ $t('pages.admin.promos.total-label', 'Total promo codes:') }}</span> {{ promos.length }}
          </div>
          <div class="row">
            <b-table
              :fields="fields"
              :items="transformedPromos"
              style="font-size: 1rem !important;"
              class="promos-table"
              responsive
            >
              <template #cell(actions)="ref">
                <b-button
                  v-if="ref.value"
                  size="sm"
                  @click="deletePromo(ref.item.id)"
                >
                  {{ $t('generic.delete', 'Delete') }}
                </b-button>
              </template>
            </b-table>
          </div>
        </div>
      </b-card-body>
    </div>
  </div>
</template>

<script>
import {ModelSelect} from 'vue-search-select';
import 'vue-search-select/dist/VueSearchSelect.css';
import debounce from "@/snippets/debounce";

export default {
  components: {
    ModelSelect,
  },
  data() {
    return {
      promos: [],
      promo: {
        code: "",
        type: "offer",
        limit: 0,
        discount: 0,
        used_times: 0,
        user: null,
        forUser: false,
      },
      isFormSubmitted: false,
      userOptions: [],
    }
  },
  computed: {
    codeState() {
      return this.promo.code.length >= 4;
    },
    limitState() {
      return this.promo.limit > 0;
    },
    discountState() {
      return this.promo.discount > 0;
    },
    typeState() {
      return this.promo.type != null;
    },
    userState() {
      return !this.promo.forUser || this.promo.user;
    },
    readyToSend() {
      return this.codeState && this.limitState && this.discountState && this.typeState && this.userState;
    },
    typeOptions() {
      return [
          { value: 'offer', text: this.$t('data.promos.offer', 'Offer') },
          { value: 'multiprovincia', text: this.$t('data.promos.multiprovince', 'Multiprovince') }
      ]
    },
    fields() {
      return [
        { key: 'id', label: this.$t('generic.id', 'ID') },
        { key: 'code', label: this.$t('generic.code', 'Code') },
        { key: 'type', label: this.$t('generic.type', 'Type') },
        { key: 'limit', label: this.$t('generic.limit', 'Limit') },
        { key: 'discount', label: this.$t('pages.admin.promos.table.discount', 'Discount') },
        { key: 'used_times', label: this.$t('pages.admin.promos.table.used-times', 'Used times') },
        { key: 'for', label: this.$t('pages.admin.promos.table.for', 'For') },
        { key: 'created_at', label: this.$t('pages.admin.promos.table.created-at', 'Created at') },
        { key: 'updated_at', label: this.$t('pages.admin.promos.table.updated-at', 'Updated at') },
        { key: 'actions', label: this.$t('pages.admin.promos.table.actions', 'Actions') },
      ]
    },
    transformedPromos() {
      return this.promos.map(
          item => ({
            ...item,
            type: this.$t(`data.promos.${item.type}`, item.type),
            for: item.user ? `${item.user.first_name} ${item.user.last_name}` : null,
            actions: !item.is_deleted,
            _rowVariant: item.is_deleted ? 'danger' : '',
          })
      )
    },
  },
  mounted() {
    this.getPromos();
    this.searchUser('');
  },
  methods: {
    searchUser(keyword) {
      this.axios
        .get('/users/admin/search', { params: { keyword } })
        .then(res => {
          if (res.data instanceof Array) {
            this.userOptions = res.data.map(i => ({
              value: i.id,
              text: `${i.first_name} ${i.last_name} (${i.email})`,
            }));
          }
        })
        .catch(err => {
          console.error(err);
        });
    },
    onSearchChange(value) {
      debounce(() => {
        this.searchUser(value);
      }, 250);
    },
    addPromo() {
      const data = {
        ...this.promo,
      };
      if (!data.forUser) {
        delete data.forUser;
        delete data.user;
      }
      this.isFormSubmitted = true;
      this.axios.post('/payment/add-promo', this.promo)
        .then(() => {
          this.getPromos();
          this.clearPromo();
          this.$toastNotification('success', '', this.$t('pages.admin.promos.promo-code-added', 'Promo code added!'));
        })
        .catch((error) => {
          this.$toastNotification('danger', '', error.response.data.error);
        });
    },
    deletePromo(id) {
      this.axios
        .delete(`/payment/delete-promo/${id}`)
        .then(() => {
          this.$toastNotification('success', '', this.$t('pages.admin.promos.promo-code-deleted', 'Promo code deleted!'));
          this.getPromos();
        })
        .catch(() => {
          this.$toastSomethingWentWrong();
        });
    },
    getPromos() {
      this.axios.get('/payment/get-promos')
        .then(response => { this.promos = response.data; })
        .catch(() => {
          this.$toastSomethingWentWrong();
        });
    },
    clearPromo() {
      this.promo.code = "";
      this.promo.type = "offer";
      this.promo.discount = 0;
      this.promo.limit = 0;
      this.promo.user = null;
      this.promo.forUser = false;
      this.isFormSubmitted = false;
    }
  }
}
</script>

<style lang="scss">
  .promos-table tbody tr {
    text-transform: uppercase;
  }
  .custom-control {
    min-height: 1.1rem;
  }
</style>